import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageType, SystemMessageTemplate } from '@/types/system-message-templates';
import { getAccessToken } from '@/lib/supabase';
import toast from 'react-hot-toast';

interface UpdateTemplateParams {
  messageType: MessageType;
  template_content: string;
}

async function updateSystemMessageTemplate(
  params: UpdateTemplateParams
): Promise<SystemMessageTemplate> {
  const token = await getAccessToken();

  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`/api/system-message-templates/${params.messageType}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ template_content: params.template_content }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update template');
  }

  return response.json();
}

export function useUpdateSystemMessageTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSystemMessageTemplate,
    onMutate: async (params) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['system-message-templates'] });

      // Snapshot the previous value
      const previousTemplates = queryClient.getQueryData<SystemMessageTemplate[]>([
        'system-message-templates',
      ]);

      // Optimistically update to the new value
      if (previousTemplates) {
        queryClient.setQueryData<SystemMessageTemplate[]>(
          ['system-message-templates'],
          previousTemplates.map((template) =>
            template.message_type === params.messageType
              ? { ...template, template_content: params.template_content }
              : template
          )
        );
      }

      // Return context with previous templates for rollback
      return { previousTemplates };
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousTemplates) {
        queryClient.setQueryData(['system-message-templates'], context.previousTemplates);
      }
      toast.error(error.message || 'Failed to update template');
    },
    onSuccess: () => {
      toast.success('Template saved successfully');
    },
    onSettled: () => {
      // Refetch to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['system-message-templates'] });
    },
  });
}
